import { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { generateSeries } from '../../data/mockMarket';

// 30-min demo candle-ish walk that lands exactly on the current live pool price,
// with the issuer's reference price drawn as a flat band for comparison.
export default function SaltPriceChart({ poolPrice, referenceUsd }: { poolPrice?: number; referenceUsd?: number }) {
  const anchor = poolPrice ?? referenceUsd ?? 1.2;

  const data = useMemo(() => {
    const raw = generateSeries(1, 60, 0.014); // deterministic gently-rising walk
    const scale = anchor / raw[raw.length - 1].price;
    const now = Date.now();
    return raw.map((p, i) => ({
      t: now - (raw.length - 1 - i) * 30_000,
      price: Number((p.price * scale).toFixed(4)),
    }));
  }, [anchor]);

  const first = data[0]?.price ?? anchor;
  const changePct = first > 0 ? ((anchor - first) / first) * 100 : 0;
  const up = changePct >= 0;
  const color = up ? '#22d3a8' : '#f5484f';

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 h-full flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foam-100">SALT / USDT</span>
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-tide-400 bg-tide-400/10 px-1.5 py-0.5 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />
              Live
            </span>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-semibold text-foam-100 tabular-nums">
              {anchor.toFixed(4)}
            </span>
            <span
              className={`flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-tide-400' : 'text-coral-400'}`}
            >
              <TrendingUp size={12} className={up ? '' : 'rotate-180'} />
              {up ? '+' : ''}
              {changePct.toFixed(2)}%
            </span>
          </div>
        </div>
        {referenceUsd !== undefined && (
          <div className="text-right">
            <div className="text-[10px] text-foam-500">발행사 기준가</div>
            <div className="text-sm font-semibold text-amber-400 tabular-nums">${referenceUsd.toFixed(2)}</div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-[180px] w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="saltFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis domain={['dataMin', 'dataMax']} hide />
            {referenceUsd !== undefined && (
              <ReferenceLine y={referenceUsd} stroke="#f59e0b" strokeDasharray="4 4" strokeOpacity={0.6} />
            )}
            <Tooltip
              contentStyle={{ background: '#0d1420', border: '1px solid #182437', borderRadius: 8, fontSize: 12 }}
              labelFormatter={() => ''}
              formatter={(value) => [`${Number(value).toFixed(4)} USDT`, 'SALT']}
            />
            <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#saltFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[10px] text-foam-600 mt-2">
        차트는 데모용 시뮬레이션입니다 — 실제 체결가는 “실시간 풀 가격”을 참고하세요.
      </div>
    </div>
  );
}
